import React, { useEffect, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { de } from 'date-fns/locale';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';

const zuText = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');
const zuIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const ausText = (t) => {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(t);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return d.getDate() === Number(m[1]) && d.getMonth() === Number(m[2]) - 1 ? zuIso(d) : null;
};
const maske = (t) => {
  const z = t.replace(/\D/g, '').slice(0, 8);
  return [z.slice(0, 2), z.slice(2, 4), z.slice(4)].filter(Boolean).join('.');
};

// Datumsfeld TT.MM.JJJJ (Eingabe oder Kalender); Wert als JJJJ-MM-TT.
export const DatumFeld = ({ value, onChange, testId, className = '', min, max, disabled, placeholder = 'TT.MM.JJJJ', jahre, onFehler }) => {
  const [text, setText] = useState(zuText(value));
  const [offen, setOffen] = useState(false);
  const [fehler, setFehlerRoh] = useState('');
  const setFehler = (x) => { setFehlerRoh(x); onFehler?.(x); };
  useEffect(() => { setText(zuText(value)); setFehlerRoh(''); }, [value]);
  const tippen = (t) => {
    const neu = maske(t);
    setText(neu);
    setFehler('');
    if (!neu) { onChange(''); return; }
    if (neu.length < 10) return;
    const iso = ausText(neu);
    if (!iso) setFehler('Ungültiges Datum');
    else if (min && iso < min) setFehler(`Frühestens ${zuText(min)}`);
    else if (max && iso > max) setFehler(`Spätestens ${zuText(max)}`);
    else onChange(iso);
  };
  const sel = value ? new Date(`${value.slice(0, 10)}T12:00:00`) : undefined;
  const heute = new Date().getFullYear();
  const [vonJahr, bisJahr] = jahre || [heute - 100, heute + 10];
  const aus = [min && { before: new Date(`${min}T00:00:00`) }, max && { after: new Date(`${max}T23:59:59`) }].filter(Boolean);
  return (
    <span className={`relative flex items-center ${className}`}>
      {fehler && <span className="absolute left-0 -bottom-4 text-[10px] text-rose-600 whitespace-nowrap" data-testid={testId ? `${testId}-fehler` : undefined}>{fehler}</span>}
      <input value={text} onChange={(e) => tippen(e.target.value)} onBlur={() => { if (!fehler) setText(zuText(value)); }} aria-invalid={!!fehler} inputMode="numeric" placeholder={placeholder} disabled={disabled}
        className="w-full min-w-0 h-full pr-7 bg-transparent text-sm focus:outline-none" data-testid={testId} />
      <Popover open={offen} onOpenChange={setOffen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} aria-label="Kalender öffnen" className="absolute right-1.5 p-1 text-[#b8893d] hover:text-[#1B3A5C] disabled:opacity-40" data-testid={testId ? `${testId}-kalender` : undefined}>
            <CalendarDays className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[200]" align="end">
          <Calendar mode="single" locale={de} selected={sel} defaultMonth={sel} weekStartsOn={1} disabled={aus.length ? aus : undefined}
            captionLayout="dropdown-buttons" fromYear={vonJahr} toYear={bisJahr}
            classNames={{ caption_dropdowns: 'flex gap-2', caption_label: 'hidden', dropdown: 'text-sm border border-gray-200 rounded px-1 py-0.5 bg-white', vhidden: 'hidden' }}
            onSelect={(d) => { if (d) { onChange(zuIso(d)); setOffen(false); } }} initialFocus />
        </PopoverContent>
      </Popover>
    </span>
  );
};

export default DatumFeld;
